import { useId, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DialogClose } from '@/components/ui/dialog'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { FormDialog, FormDialogContent } from '@/components/ui/form-dialog'
import { Textarea } from '@/components/ui/textarea'
import type { Inquiry } from '@/lib/api/types'
import { quickEditErrorMessage } from '@/lib/quick-edit'

/** As the API caps it. */
const REASON_MAX = 500

/**
 * "Cancel this inquiry?": a reason, what cancelling does, and a button that
 * means it. `onConfirm` does the cancelling; if it throws, the dialog stays
 * open and says why.
 */
export function CancelInquiryDialog({
  inquiry,
  label,
  count = 1,
  open,
  onOpenChange,
  onConfirm,
}: {
  inquiry: Pick<Inquiry, 'tasks'>
  /** How the inquiry is named to the person cancelling it. */
  label: string
  /** More than one: the same reason goes on each. */
  count?: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string) => Promise<void>
}) {
  const id = useId()
  const [reason, setReason] = useState('')
  const [missing, setMissing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const many = count > 1
  const openTasks = (inquiry.tasks ?? []).filter(
    (task) => task.status === 'TODO' || task.status === 'IN_PROGRESS',
  )

  async function submit(event: FormEvent) {
    event.preventDefault()
    const why = reason.trim()
    if (!why) {
      setMissing(true)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onConfirm(why)
      onOpenChange(false)
    } catch (cause) {
      setError(quickEditErrorMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormDialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <FormDialogContent
        title={many ? `Cancel ${count} inquiries?` : 'Cancel this inquiry?'}
        description={label}
        submitLabel="Cancel inquiry"
        busy={busy}
        error={error}
        errorTitle="Not cancelled"
        onSubmit={submit}
        footer={
          <>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={busy}>
                Keep it
              </Button>
            </DialogClose>
            <Button type="submit" variant="destructive" disabled={busy}>
              {busy && <Loader2 className="animate-spin" />}
              {busy ? 'Cancelling' : many ? `Cancel ${count} inquiries` : 'Cancel inquiry'}
            </Button>
          </>
        }
      >
        <Field data-invalid={missing || undefined}>
          <FieldLabel htmlFor={`${id}-reason`} required>
            Reason
          </FieldLabel>
          <Textarea
            id={`${id}-reason`}
            rows={3}
            value={reason}
            maxLength={REASON_MAX}
            aria-invalid={missing || undefined}
            aria-describedby={`${id}-reason-help`}
            placeholder="The client bought locally."
            onChange={(event) => {
              setReason(event.target.value)
              if (event.target.value.trim()) setMissing(false)
            }}
          />
          <FieldDescription id={`${id}-reason-help`}>
            Shown on the inquiry and its timeline. {reason.length}/{REASON_MAX}
          </FieldDescription>
          {missing && <FieldError>Say why it is cancelled.</FieldError>}
        </Field>
        <div className="space-y-2 text-sm">
          <p className="font-medium">{many ? 'Cancelling them:' : 'Cancelling it:'}</p>
          {many ? (
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>marks each Cancelled, with this reason, your name and the time;</li>
              <li>closes their open tasks;</li>
              <li>stops their follow-up alerts, and takes them off the Attention list.</li>
            </ul>
          ) : (
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>marks it Cancelled, with your reason, your name and the time;</li>
              <li>
                {openTasks.length
                  ? `closes its open ${openTasks.length === 1 ? 'task' : 'tasks'}: ${openTasks
                      .map((task) => task.title)
                      .join(', ')};`
                  : 'closes any open tasks;'}
              </li>
              <li>stops its follow-up alerts, and takes it off the Attention list.</li>
            </ul>
          )}
          <p className="text-muted-foreground">
            {many ? 'An admin can reopen any of them.' : 'An admin can reopen it.'}
          </p>
        </div>
      </FormDialogContent>
    </FormDialog>
  )
}
